import React from 'react'
const { useState, useEffect, useMemo, useRef } = React;

import { getAuthHeaders } from '../globals.js';
import {
  METHOD_APPLIES_TO, METHOD_STATUSES, METHOD_SOURCE_TYPES, METHOD_NOTES_MAX, methodLabel,
  METHOD_TOOL_TYPES, METHOD_AREAS, METHOD_MODALITIES, METHOD_TOOL_TEXT_LIMITS, COACHING_PLACES, METHOD_OBSERVATION_MAX,
  SOURCE_KINDS, SOURCE_PROCESSING, SOURCE_TEXT_LIMITS, SOURCE_EXCERPTS_MAX, sourceFileKind, SOURCE_FILE_MESSAGES,
  METHOD_INDUSTRIES, METHOD_TEAM_SIZES, METHOD_SALE_TYPES, METHOD_COUNTRIES, METHOD_ENTRY_TYPES, METHOD_SD_MODULES,
  METHOD_REGIONS, methodTextLimits,
} from '../methodLibraryConfig.js';
import { inputSt, textareaSt, labelSt, cardSt, mutedSt, badgeSt, warnSt, fmtDate, Field, SubTabs } from './diagnostic/shared.jsx';
import { extractTranscriptText } from '../diagnosticTranscriptFile.js';
import { PageHeader } from './ui/States.jsx';

// "Biblioteca": Ale's method library (Oike administrator only, central base).
// The AI reads the active entries when it writes: P3 and P5 of the Diagnostic,
// the message generators, the proposal generators and the coaching session
// proposals. The rubric is not here: it changes only in code
// (src/diagnosticConfig.js, RUBRIC_VERSION).
// Tools: where an entry is used in Oike (Dónde se usa), its area and its
// modality are three separate choices. Sources: a saved reference is not read
// content; the AI only works with the text Ale provides.

const ERRORS = {
  unauthorized: 'Tu sesión venció. Vuelve a iniciar sesión.',
  forbidden: 'Solo el administrador de Oike puede usar la biblioteca.',
  not_found: 'No se encontró la entrada. Actualiza la lista.',
  too_large: 'El texto es demasiado largo para enviarlo.',
  'invalid_input: not current': 'La entrada cambió mientras la editabas. Ábrela de nuevo.',
  'invalid_input: missing title': 'Falta el título.',
  'invalid_input: missing technique': 'Falta la técnica.',
  'invalid_input: missing whenToUse': 'Falta cuándo usarla.',
  'invalid_input: missing example': 'Falta el ejemplo.',
  'invalid_input: missing appliesTo': 'Elige al menos un lugar donde aplica.',
  'invalid_input: missing sourceType': 'Elige el tipo de fuente o vincula una fuente de la pestaña Fuentes.',
  'invalid_input: missing purpose': 'Falta el propósito de la herramienta.',
  'invalid_input: missing areas': 'Elige al menos un área (personal, profesional, negocios o ventas).',
  'invalid_input: missing modality': 'Elige la modalidad (individual, equipo o ambas).',
  'invalid_input: missing toolType': 'Elige el tipo de herramienta.',
  'invalid_input: schema pending': 'Faltan campos nuevos en Airtable (de herramienta o de criterios por cliente): aplica la migración (docs/coaching/configuracion.md) o pide los campos de System Design (docs/diagnostic/airtable-schema.md).',
  'invalid_input: no content': 'Esta fuente es solo una referencia: agrega tus notas o fragmentos para que la IA trabaje con ese texto.',
  'invalid_input: missing kind': 'Elige el tipo de fuente.',
  'invalid_input: missing text': 'Escribe la observación.',
  'invalid_input: too many excerpts': `Hasta ${SOURCE_EXCERPTS_MAX} fragmentos por fuente.`,
  'invalid_input: invalid industries': 'La lista de industrias no es válida.',
  'invalid_input: invalid teamSizes': 'La lista de tamaños no es válida.',
  'invalid_input: invalid saleTypes': 'La lista de tipos de venta no es válida.',
  'invalid_input: invalid countries': 'La lista de países no es válida.',
  'invalid_input: invalid sdModules': 'La lista de módulos no es válida.',
  'invalid_input: missing notes': 'Pega o dicta tus notas.',
  'invalid_input: notes too long': `Las notas pasan de ${METHOD_NOTES_MAX.toLocaleString('es')} caracteres. Recórtalas.`,
  'invalid_input: ai failed': 'La IA no respondió. Vuelve a intentarlo.',
  'invalid_input: ai unavailable': 'La IA no está configurada en este entorno.',
  'invalid_input: no proposals': 'La IA no encontró ninguna técnica en las notas.',
};

export async function call(body) {
  const res = await fetch('/api/method-library', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = data?.error || '';
    throw new Error(ERRORS[code] || (code.startsWith('invalid_input: too long') ? 'Un campo pasa del largo máximo.' : res.status === 403 ? ERRORS.forbidden : 'No se pudo completar. Vuelve a intentarlo.'));
  }
  return data;
}

const EMPTY_TOOL = { toolType: null, purpose: '', areas: [], modality: null, topics: [], requiresTraining: false, sourceRefs: [], steps: '', duration: '', materials: '', openingQuestions: '', closingQuestions: '', adaptation: '', facilitationNotes: '', scopeNote: '' };
const EMPTY = {
  title: '', technique: '', whenToUse: '', whenNotToUse: '', example: '', source: '', appliesTo: [], sourceType: null, clientVisible: false,
  entryType: 'technique', industries: [], teamSizes: [], saleTypes: [], countries: [], sdModules: [],
  ...EMPTY_TOOL,
};
const TOOL_KEYS = Object.keys(EMPTY_TOOL);
const isTool = (e) => !!e.toolType || (e.appliesTo || []).some(k => COACHING_PLACES.includes(k));
// Every field the panel edits: the entry, its client criteria (Fase 7) and its tool fields
const EDITABLE = Object.keys(EMPTY);
const editableOf = (e) => Object.fromEntries(EDITABLE.map(k => [k, e[k] ?? EMPTY[k]]));

/** What an entry is for, in words: "General" or its criteria. */
function criteriaOf(e) {
  const out = [];
  if (e.industries?.length) out.push(`Industria: ${e.industries.map(k => methodLabel(METHOD_INDUSTRIES, k)).join(', ')}`);
  if (e.teamSizes?.length) out.push(`Equipo: ${e.teamSizes.map(k => methodLabel(METHOD_TEAM_SIZES, k)).join(', ')}`);
  if (e.saleTypes?.length) out.push(`Venta: ${e.saleTypes.map(k => (k === 'complex' ? 'compleja' : 'transaccional')).join(', ')}`);
  if (e.countries?.length) out.push(`País: ${e.countries.map(k => methodLabel(METHOD_COUNTRIES, k)).join(', ')}`);
  return out;
}
const isGeneral = (e) => !criteriaOf(e).length;

function ChipGroup({ label, hint, options, value, onChange }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter(x => x !== k) : [...value, k]);
  return (
    <div>
      <span style={labelSt}>{label}</span>
      <div style={{ display: 'flex', gap: '4px 12px', flexWrap: 'wrap' }}>
        {options.map(o => (
          <label key={o.key} style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={value.includes(o.key)} onChange={() => toggle(o.key)} /> {o.label}
          </label>
        ))}
      </div>
      {hint && <div style={{ ...mutedSt, fontSize: 11, marginTop: 3 }}>{hint}</div>}
    </div>
  );
}
const statusColor = { draft: 'rgba(245,158,11,0.15)', active: 'rgba(91,191,181,0.15)', archived: 'rgba(30,42,44,0.06)' };
const EVENT_LABELS = { create: 'Creada', edit: 'Editada', approve: 'Aprobada', archive: 'Archivada', unarchive: 'Vuelve a borrador' };

function StatusBadge({ status }) {
  return <span style={{ ...badgeSt(status === 'active'), background: statusColor[status] }}>{methodLabel(METHOD_STATUSES, status)}</span>;
}

function Counter({ value, max }) {
  const n = (value || '').length;
  return <span style={{ ...mutedSt, fontSize: 11, color: n > max ? 'var(--globant-danger)' : undefined }}>{n}/{max}</span>;
}

/** The entry form (by hand, or a proposal). Essential fields first; the rest grouped. */
function EntryForm({ value, onChange, toolFields = true, sources = [] }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  // A framework admits longer text; the tool fields have their own limits
  const limits = methodTextLimits(value.entryType);
  const limit = (k) => limits[k] ?? METHOD_TOOL_TEXT_LIMITS[k];
  const text = (k, label, rows = 3, hint) => (
    <Field label={<>{label} <Counter value={value[k]} max={limit(k)} /></>} hint={hint}>
      {rows === 1
        ? <input style={inputSt} value={value[k] || ''} maxLength={limit(k)} onChange={e => set(k, e.target.value)} />
        : <textarea style={{ ...textareaSt, minHeight: rows * 22 }} value={value[k] || ''} maxLength={limit(k)} onChange={e => set(k, e.target.value)} />}
    </Field>
  );
  // A framework → technique: the longer text stays only if it fits the technique limit
  const setType = (t) => {
    const lim = methodTextLimits(t);
    const next = { ...value, entryType: t };
    for (const k of ['technique', 'whenToUse', 'whenNotToUse', 'example']) next[k] = (next[k] || '').slice(0, lim[k]);
    onChange(next);
  };
  const toggle = (k, x) => set(k, (value[k] || []).includes(x) ? value[k].filter(y => y !== x) : [...(value[k] || []), x]);
  const tool = isTool(value);
  const refs = value.sourceRefs || [];
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Field label="Tipo de entrada" hint="Un framework admite más texto en la técnica y entra entero o no entra (si no cabe en el tope, queda afuera).">
        <select style={{ ...inputSt, maxWidth: 360 }} value={value.entryType || 'technique'} onChange={e => setType(e.target.value)}>
          {METHOD_ENTRY_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </Field>
      {text('title', 'Título', 1)}
      {toolFields && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <Field label="Tipo de herramienta">
            <select style={inputSt} value={value.toolType || ''} onChange={e => set('toolType', e.target.value || null)}>
              <option value="">— (entrada comercial)</option>
              {METHOD_TOOL_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Modalidad">
            <select style={inputSt} value={value.modality || ''} onChange={e => set('modality', e.target.value || null)}>
              <option value="">—</option>
              {METHOD_MODALITIES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Field>
        </div>
      )}
      {toolFields && tool && text('purpose', 'Propósito', 2)}
      {text('technique', tool ? 'Cómo se usa (en tus palabras)' : value.entryType === 'framework' ? 'Framework (en tus palabras)' : 'Técnica (en tus palabras)', value.entryType === 'framework' ? 9 : 5)}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
        {text('whenToUse', 'Cuándo usarla', 3)}
        {text('whenNotToUse', tool ? 'Cuándo evitarla' : 'Cuándo no usarla', 3)}
      </div>
      {text('example', tool ? 'Ejemplo de aplicación' : 'Ejemplo concreto', 4)}
      <div>
        <span style={labelSt}>Dónde se usa en Oike</span>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {METHOD_APPLIES_TO.filter(a => toolFields || !COACHING_PLACES.includes(a.key)).map(a => (
            <label key={a.key} style={{ fontSize: 13, display: 'flex', gap: 4, alignItems: 'center' }}>
              <input type="checkbox" checked={value.appliesTo.includes(a.key)} onChange={() => toggle('appliesTo', a.key)} /> {a.label}
            </label>
          ))}
        </div>
      </div>
      {toolFields && (
        <div>
          <span style={labelSt}>Área (puede ser más de una)</span>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {METHOD_AREAS.map(a => <label key={a.key} style={{ fontSize: 13 }}><input type="checkbox" checked={(value.areas || []).includes(a.key)} onChange={() => toggle('areas', a.key)} /> {a.label}</label>)}
          </div>
        </div>
      )}
      <div style={{ border: '1px solid var(--globant-border)', borderRadius: 8, padding: 10, display: 'grid', gap: 10 }}>
        <div>
          <span style={labelSt}>Para quién es</span>
          <div style={{ ...mutedSt, fontSize: 11 }}>Todo es opcional. Sin ninguna industria, tamaño, tipo de venta ni país, la entrada es general y sirve para todos los clientes. Si marcas un criterio, solo entra para los clientes que lo cumplen.</div>
        </div>
        <ChipGroup label="Industria" options={METHOD_INDUSTRIES} value={value.industries || []} onChange={v => set('industries', v)} />
        <ChipGroup label="Tamaño del equipo comercial" options={METHOD_TEAM_SIZES} value={value.teamSizes || []} onChange={v => set('teamSizes', v)} />
        <ChipGroup label="Tipo de venta" options={METHOD_SALE_TYPES} value={value.saleTypes || []} onChange={v => set('saleTypes', v)} />
        <ChipGroup label="País" options={METHOD_COUNTRIES} value={value.countries || []} onChange={v => set('countries', v)}
          hint={`Una región incluye sus países: Latinoamérica (${METHOD_REGIONS.latam.length}), Europa (${METHOD_REGIONS.europe.length}) y Norteamérica (${METHOD_REGIONS.northAmerica.length}). México está solo en Latinoamérica.`} />
        {value.appliesTo.includes('systemDesign') && (
          <ChipGroup label="Módulos de System Design" options={METHOD_SD_MODULES} value={value.sdModules || []} onChange={v => set('sdModules', v)}
            hint="Sin ninguno, sirve para todos los módulos." />
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 220px) 1fr', gap: 12 }}>
        <Field label="Tipo de fuente">
          <select style={inputSt} value={value.sourceType || ''} onChange={e => set('sourceType', e.target.value || null)}>
            <option value="">—</option>
            {METHOD_SOURCE_TYPES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </Field>
        {text('source', 'Nombre de la fuente (opcional)', 1)}
      </div>
      {toolFields && sources.length > 0 && (
        <div>
          <span style={labelSt}>Fuentes de la biblioteca</span>
          {refs.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              <select style={{ ...inputSt, flex: 1 }} value={r.sourceId} onChange={e => set('sourceRefs', refs.map((x, j) => (j === i ? { ...x, sourceId: e.target.value } : x)))}>
                {sources.map(s => <option key={s.id} value={s.id}>{s.title}{s.creator ? ` · ${s.creator}` : ''}</option>)}
              </select>
              <input style={{ ...inputSt, width: 160 }} placeholder="Capítulo, página o minuto" value={r.locator || ''} maxLength={SOURCE_TEXT_LIMITS.locator} onChange={e => set('sourceRefs', refs.map((x, j) => (j === i ? { ...x, locator: e.target.value } : x)))} />
              <button className="action-btn" onClick={() => set('sourceRefs', refs.filter((_, j) => j !== i))}>✕</button>
            </div>
          ))}
          <button className="action-btn" style={{ fontSize: 11 }} onClick={() => set('sourceRefs', [...refs, { sourceId: sources[0].id, locator: '' }])}>+ Vincular fuente</button>
        </div>
      )}
      {toolFields && (
        <>
          <details>
            <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--globant-muted)' }}>Pasos, duración, materiales y preguntas</summary>
            <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
              {text('steps', 'Pasos', 4)}
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 200px) 1fr', gap: 12 }}>{text('duration', 'Duración', 1)}{text('materials', 'Materiales', 2)}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>{text('openingQuestions', 'Preguntas de apertura', 3)}{text('closingQuestions', 'Preguntas de cierre', 3)}</div>
            </div>
          </details>
          <details>
            <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--globant-muted)' }}>Tu adaptación, facilitación, temas, alcance y formación</summary>
            <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
              {text('adaptation', 'Mi adaptación', 3)}
              {text('facilitationNotes', 'Notas de facilitación', 3)}
              <Field label="Temas (separados por coma)" hint="Sirven para buscar. Por ejemplo: límites, confianza, comunicación.">
                <input style={inputSt} value={(value.topics || []).join(', ')} onChange={e => set('topics', e.target.value.split(',').map(x => x.trim()).filter(Boolean))} />
              </Field>
              {text('scopeNote', 'Alcance de uso', 2, 'Cuándo excede el acompañamiento y conviene derivar.')}
              <label style={{ fontSize: 13 }}><input type="checkbox" checked={!!value.requiresTraining} onChange={e => set('requiresTraining', e.target.checked)} /> Requiere formación específica para usarla</label>
            </div>
          </details>
        </>
      )}
      <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <input type="checkbox" checked={value.clientVisible} onChange={e => set('clientVisible', e.target.checked)} style={{ marginTop: 3 }} />
        <span>Visible en funciones para clientes<br /><span style={mutedSt}>Si la marcas, la usan también los generadores de mensajes y propuestas de las bases de los clientes.</span></span>
      </label>
    </div>
  );
}

function History({ entry }) {
  const versions = entry.history?.versions || [];
  const events = entry.history?.events || [];
  const [open, setOpen] = useState(null);
  return (
    <div>
      <span style={labelSt}>Historial</span>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
        {events.map((e, i) => (
          <li key={i}>{EVENT_LABELS[e.event] || e.event} · v{e.version} · {fmtDate(e.at, true)} · {e.by}</li>
        ))}
      </ul>
      {versions.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <span style={labelSt}>Versiones anteriores</span>
          {versions.map(v => (
            <div key={v.version} style={{ borderTop: '1px solid var(--globant-border)', padding: '6px 0' }}>
              <button className="action-btn" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setOpen(open === v.version ? null : v.version)}>
                {open === v.version ? '▾' : '▸'} v{v.version} · {methodLabel(METHOD_STATUSES, v.status)} · {fmtDate(v.updatedAt, true)}
              </button>
              {open === v.version && (
                <div style={{ fontSize: 12, marginTop: 6, whiteSpace: 'pre-wrap', display: 'grid', gap: 4 }}>
                  <div><b>{v.title}</b></div>
                  <div><b>Técnica:</b> {v.technique}</div>
                  {v.whenToUse && <div><b>Cuándo usarla:</b> {v.whenToUse}</div>}
                  {v.whenNotToUse && <div><b>Cuándo no:</b> {v.whenNotToUse}</div>}
                  {v.example && <div><b>Ejemplo:</b> {v.example}</div>}
                  <div style={mutedSt}>Aplica en: {(v.appliesTo || []).map(k => methodLabel(METHOD_APPLIES_TO, k)).join(', ') || '—'} · {v.clientVisible ? 'visible para clientes' : 'solo Oike'} · {isGeneral(v) ? 'general' : criteriaOf(v).join(' · ')}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** One entry: view, edit, approve, archive and its history. */
// Exported for Mi biblioteca (the owner's single library), which opens the same editor with the same permissions
export function EntryDetail({ id, onBack, onChanged, toolFields, sources, backLabel = '← Volver a la lista' }) {
  const [observation, setObservation] = useState('');
  const [entry, setEntry] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { call({ action: 'get', id }).then(d => setEntry(d.entry)).catch(e => setError(e.message)); }, [id]);
  const act = async (body) => {
    setBusy(true); setError('');
    let ok = false;
    try { const d = await call({ ...body, id, baseVersion: entry.version }); setEntry(d.entry); setForm(null); onChanged(d.entry); ok = true; }
    catch (e) { setError(e.message); }
    setBusy(false);
    return ok;
  };
  if (!entry) return <div style={cardSt}>{error || 'Cargando…'}</div>;
  const tool = isTool(entry);
  const toolInfo = [['purpose', 'Propósito'], ['steps', 'Pasos'], ['duration', 'Duración'], ['materials', 'Materiales'], ['openingQuestions', 'Preguntas de apertura'], ['closingQuestions', 'Preguntas de cierre'], ['adaptation', 'Mi adaptación'], ['facilitationNotes', 'Notas de facilitación'], ['scopeNote', 'Alcance de uso']].filter(([k]) => entry[k]);
  return (
    <div style={cardSt}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <button className="action-btn" onClick={onBack}>{backLabel}</button>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {!form && <button className="action-btn" disabled={busy} onClick={() => setForm(editableOf(entry))}>✏️ Editar</button>}
          {!form && entry.status === 'draft' && <button className="action-btn btn-primary" disabled={busy} onClick={() => act({ action: 'approve' })}>✓ Aprobar</button>}
          {!form && entry.status !== 'archived' && <button className="action-btn" disabled={busy} onClick={() => window.confirm('¿Archivar esta entrada? Deja de entrar en los prompts.') && act({ action: 'archive' })}>Archivar</button>}
          {!form && entry.status === 'archived' && <button className="action-btn" disabled={busy} onClick={() => act({ action: 'unarchive' })}>Volver a borrador</button>}
        </div>
      </div>
      {error && <div style={{ ...warnSt, padding: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <StatusBadge status={entry.status} /><span style={mutedSt}>v{entry.version}</span>
        {entry.approvedBy && <span style={mutedSt}>· aprobada por {entry.approvedBy} el {fmtDate(entry.approvedAt, true)}</span>}
        {entry.origin === 'from_notes' && <span style={mutedSt}>· creada desde notas</span>}
      </div>
      {form ? (
        <>
          {entry.status === 'active' && <div style={{ ...mutedSt, marginBottom: 10 }}>Es una entrada activa: al guardar sube a v{entry.version + 1} y sigue activa. La versión actual queda en el historial.</div>}
          <EntryForm value={form} onChange={setForm} toolFields={toolFields} sources={sources} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="action-btn btn-primary" disabled={busy} onClick={() => act({ action: 'update', entry: form })}>Guardar</button>
            <button className="action-btn" disabled={busy} onClick={() => setForm(null)}>Cancelar</button>
          </div>
        </>
      ) : (
        <div style={{ display: 'grid', gap: 10, fontSize: 13, whiteSpace: 'pre-wrap' }}>
          <h3 style={{ margin: 0 }}>{entry.title}</h3>
          <div><span style={labelSt}>Técnica</span>{entry.technique || '—'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
            <div><span style={labelSt}>Cuándo usarla</span>{entry.whenToUse || '—'}</div>
            <div><span style={labelSt}>Cuándo no usarla</span>{entry.whenNotToUse || '—'}</div>
          </div>
          <div><span style={labelSt}>Ejemplo</span>{entry.example || '—'}</div>
          {toolInfo.map(([k, l]) => <div key={k}><span style={labelSt}>{l}</span>{entry[k]}</div>)}
          {tool && (
            <div style={mutedSt}>
              {entry.toolType ? methodLabel(METHOD_TOOL_TYPES, entry.toolType) : 'Sin tipo'} · Área: {(entry.areas || []).map(k => methodLabel(METHOD_AREAS, k)).join(', ') || '—'} · Modalidad: {entry.modality ? methodLabel(METHOD_MODALITIES, entry.modality) : '—'}
              {entry.requiresTraining ? ' · Requiere formación específica' : ''}{entry.topics?.length ? ` · Temas: ${entry.topics.join(', ')}` : ''}
            </div>
          )}
          <div style={mutedSt}>
            {entry.entryType === 'framework' ? 'Framework · ' : ''}
            {isGeneral(entry) ? 'General (sirve para todos los clientes)' : criteriaOf(entry).join(' · ')}
            {entry.sdModules?.length ? ` · Módulos: ${entry.sdModules.map(k => methodLabel(METHOD_SD_MODULES, k)).join(', ')}` : ''}
          </div>
          <div style={mutedSt}>
            Dónde se usa: {entry.appliesTo.map(k => methodLabel(METHOD_APPLIES_TO, k)).join(', ') || '—'} ·
            Fuente: {entry.sourceType ? methodLabel(METHOD_SOURCE_TYPES, entry.sourceType) : '—'}{entry.source ? ` (${entry.source})` : ''} ·
            {entry.clientVisible ? ' Visible en funciones para clientes' : ' Solo en funciones de Oike'}
          </div>
          {entry.sourceRefs?.length > 0 && (
            <div style={mutedSt}>Fuentes vinculadas: {entry.sourceRefs.map(r => `${sources.find(x => x.id === r.sourceId)?.title || 'fuente'}${r.locator ? ` (${r.locator})` : ''}`).join(' · ')}</div>
          )}
        </div>
      )}
      {toolFields && (
        <div style={{ marginTop: 16 }}>
          <span style={labelSt}>Observaciones reutilizables</span>
          <div style={mutedSt}>Lo que aprendes usando la herramienta, en general. Sin nombres ni detalles de sesiones: esos quedan en las notas privadas del proceso. No entran en los prompts ni cambian la versión.</div>
          {(entry.observations || []).map(o => (
            <div key={o.id} style={{ fontSize: 12, borderBottom: '1px solid var(--globant-border)', padding: '4px 0' }}>
              {o.text} <span style={mutedSt}>· {fmtDate(o.at)}</span>
              <button className="action-btn" style={{ fontSize: 10, padding: '1px 6px', marginLeft: 6 }} onClick={() => act({ action: 'removeObservation', observationId: o.id })}>✕</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <input style={inputSt} value={observation} maxLength={METHOD_OBSERVATION_MAX} placeholder="Por ejemplo: funciona mejor con tarjetas impresas" onChange={e => setObservation(e.target.value)} />
            <button className="action-btn" disabled={busy || !observation.trim()} onClick={() => act({ action: 'addObservation', text: observation }).then(ok => ok && setObservation(''))}>Agregar</button>
          </div>
        </div>
      )}
      <div style={{ marginTop: 16 }}><History entry={entry} /></div>
    </div>
  );
}

export function NewEntry({ onSaved, onCancel, toolFields, sources }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true); setError('');
    try { const d = await call({ action: 'create', entry: form, origin: 'manual' }); onSaved(d.entry); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <div style={cardSt}>
      <h3 style={{ marginTop: 0 }}>Nueva entrada</h3>
      <p style={mutedSt}>Se guarda como borrador. Entra en los prompts recién cuando la apruebes.</p>
      {error && <div style={{ ...warnSt, padding: 10 }}>{error}</div>}
      <EntryForm value={form} onChange={setForm} toolFields={toolFields} sources={sources} />
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="action-btn btn-primary" disabled={busy} onClick={save}>Guardar borrador</button>
        <button className="action-btn" disabled={busy} onClick={onCancel}>Cancelar</button>
      </div>
    </div>
  );
}

/** "Crear desde mis notas": the notes stay in this screen only; the server never saves them. */
export function FromNotes({ onSaved, toolFields }) {
  const [notes, setNotes] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [source, setSource] = useState('');
  const [proposals, setProposals] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [listening, setListening] = useState(false);
  const recognition = useRef(null);
  const Speech = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

  useEffect(() => () => recognition.current?.stop(), []);

  const dictate = () => {
    if (listening) { recognition.current?.stop(); return; }
    const r = new Speech();
    r.lang = 'es-AR'; r.continuous = true; r.interimResults = false;
    r.onresult = (e) => {
      const said = Array.from(e.results).slice(e.resultIndex).filter(x => x.isFinal).map(x => x[0].transcript).join(' ');
      if (said) setNotes(n => (n ? `${n} ${said}` : said).slice(0, METHOD_NOTES_MAX));
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recognition.current = r; r.start(); setListening(true);
  };

  const propose = async () => {
    setBusy(true); setError('');
    try {
      const d = await call({ action: 'proposeFromNotes', notes, sourceType: sourceType || null, source });
      setProposals(d.proposals.map((p, i) => ({ ...p, _key: `${Date.now()}-${i}` })));
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const saveOne = async (p) => {
    setBusy(true); setError('');
    try {
      const { _key, ...entry } = p;
      const d = await call({ action: 'create', entry: toolFields ? entry : Object.fromEntries(Object.entries(entry).filter(([k]) => !TOOL_KEYS.includes(k))), origin: 'from_notes' });
      setProposals(list => list.filter(x => x._key !== _key));
      onSaved(d.entry);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  return (
    <div style={cardSt}>
      <h3 style={{ marginTop: 0 }}>Crear desde mis notas</h3>
      <div style={{ ...warnSt, padding: 10, fontSize: 13 }}>
        Escribí la técnica en tus palabras; no pegues el contenido completo de un curso o un libro.
      </div>
      <p style={mutedSt}>Pega o dicta lo que anotaste de un curso o de una experiencia. La IA te propone una o varias entradas; las editas y las guardas como borrador. Las notas no se guardan: solo las entradas que guardes.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 220px) 1fr', gap: 12, marginBottom: 12 }}>
        <Field label="Fuente">
          <select style={inputSt} value={sourceType} onChange={e => setSourceType(e.target.value)}>
            <option value="">—</option>
            {METHOD_SOURCE_TYPES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Nombre del curso o libro (opcional)">
          <input style={inputSt} value={source} maxLength={methodTextLimits().source} onChange={e => setSource(e.target.value)} />
        </Field>
      </div>
      <Field label={<>Notas <Counter value={notes} max={METHOD_NOTES_MAX} /></>}>
        <textarea style={{ ...textareaSt, minHeight: 180 }} value={notes} maxLength={METHOD_NOTES_MAX} onChange={e => setNotes(e.target.value)} />
      </Field>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {Speech && <button className="action-btn" disabled={busy} onClick={dictate}>{listening ? '⏹ Detener dictado' : '🎙 Dictar'}</button>}
        <button className="action-btn btn-primary" disabled={busy || !notes.trim()} onClick={propose}>{busy && !proposals.length ? 'Pensando…' : '✨ Proponer entradas'}</button>
        {notes && <button className="action-btn" disabled={busy} onClick={() => setNotes('')}>Borrar notas</button>}
      </div>
      {error && <div style={{ ...warnSt, padding: 10, marginTop: 10 }}>{error}</div>}
      {proposals.map((p, i) => (
        <div key={p._key} style={{ ...cardSt, marginTop: 16, background: 'rgba(30,42,44,0.02)' }}>
          <div style={{ ...mutedSt, marginBottom: 8 }}>Propuesta {i + 1} de {proposals.length} · todavía no está guardada</div>
          <EntryForm value={{ ...EMPTY, ...p }} toolFields={toolFields} onChange={v => setProposals(list => list.map(x => (x._key === p._key ? { ...v, _key: p._key } : x)))} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="action-btn btn-primary" disabled={busy} onClick={() => saveOne(p)}>Guardar como borrador</button>
            <button className="action-btn" disabled={busy} onClick={() => setProposals(list => list.filter(x => x._key !== p._key))}>Descartar</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Sources ──

const EMPTY_SOURCE = { title: '', creator: '', kind: 'book', reference: '', topics: [], notes: '', excerpts: [] };
const processingLabel = (k) => SOURCE_PROCESSING.find(x => x.key === k) || SOURCE_PROCESSING[0];

/** Tool proposals from one source: review, adapt or discard each one; save as drafts. Also used by Mi espacio ("Convertir en herramienta"). */
export function SourceProposals({ proposals, setProposals, onSaved, sources }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async (p) => {
    setBusy(true); setError('');
    try {
      const { _key, provenance, checks, ...entry } = p;
      const d = await call({ action: 'create', entry, origin: 'from_source' });
      setProposals(list => list.filter(x => x._key !== _key));
      onSaved(d.entry);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <>
      {error && <div style={{ ...warnSt, padding: 10, marginTop: 10 }}>{error}</div>}
      {proposals.map((p, i) => (
        <div key={p._key} style={{ ...cardSt, marginTop: 16, background: 'rgba(30,42,44,0.02)' }}>
          <div style={{ ...mutedSt, marginBottom: 8 }}>Propuesta {i + 1} de {proposals.length} · todavía no está guardada</div>
          <div style={{ fontSize: 12, display: 'grid', gap: 4, marginBottom: 10, borderLeft: '2px solid var(--globant-border)', paddingLeft: 8 }}>
            <div><b>De la fuente:</b> {p.provenance?.fromSource || '—'}</div>
            <div><b>De tus notas:</b> {p.provenance?.fromNotes || '—'}</div>
            <div><b>Sugerencias de la IA:</b> {p.provenance?.aiSuggestions || '—'}</div>
          </div>
          {(p.checks?.unverifiedQuotes > 0 || p.checks?.scientificClaims?.length > 0) && (
            <div style={{ ...warnSt, padding: 10, fontSize: 12 }}>
              {p.checks.unverifiedQuotes > 0 && <div>Se quitaron las comillas de {p.checks.unverifiedQuotes} {p.checks.unverifiedQuotes === 1 ? 'frase que no está' : 'frases que no están'} en tu texto: no son citas.</div>}
              {p.checks.scientificClaims?.length > 0 && <div>Menciona respaldo científico que tu texto no aporta ({p.checks.scientificClaims.join(', ')}): revísalo o quítalo.</div>}
            </div>
          )}
          <EntryForm value={{ ...EMPTY, ...p }} sources={sources} onChange={v => setProposals(list => list.map(x => (x._key === p._key ? { ...x, ...v } : x)))} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="action-btn btn-primary" disabled={busy} onClick={() => save(p)}>Guardar como borrador</button>
            <button className="action-btn" disabled={busy} onClick={() => setProposals(list => list.filter(x => x._key !== p._key))}>Descartar</button>
          </div>
        </div>
      ))}
    </>
  );
}

// Exported for Mi biblioteca, like EntryDetail
export function SourceEditor({ id, onBack, onSaved, sources, onToolSaved, backLabel = '← Fuentes' }) {
  const [form, setForm] = useState(id ? null : EMPTY_SOURCE);
  const [source, setSource] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [proposals, setProposals] = useState([]);
  useEffect(() => { if (id) call({ action: 'getSource', id }).then(d => { setSource(d.source); setForm(d.source); }).catch(e => setError(e.message)); }, [id]);
  if (!form) return <div style={cardSt}>{error || 'Cargando…'}</div>;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    setBusy(true); setError('');
    try { const d = await call({ action: 'saveSource', id: source?.id, source: form }); setSource(d.source); setForm(d.source); onSaved(d.source); } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const propose = async () => {
    setBusy(true); setError('');
    try { const d = await call({ action: 'proposeFromSource', id: source.id }); setSource(d.source); onSaved(d.source); setProposals(d.proposals.map((p, i) => ({ ...p, _key: `${Date.now()}-${i}` }))); } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const importFile = async (file) => {
    setError('');
    const kind = sourceFileKind(file.name);
    if (!['text', 'docx'].includes(kind)) { setError(SOURCE_FILE_MESSAGES[kind]); return; }
    try {
      const textIn = (await extractTranscriptText(kind === 'docx' ? 'archivo.docx' : 'archivo.txt', await file.arrayBuffer())).trim().slice(0, SOURCE_TEXT_LIMITS.excerpt);
      if (textIn) set('excerpts', [...form.excerpts, { text: textIn, locator: file.name.slice(0, SOURCE_TEXT_LIMITS.locator) }].slice(0, SOURCE_EXCERPTS_MAX));
    } catch { setError('No se pudo leer el archivo. Usa .txt, .md o .docx.'); }
  };
  const saved = source && JSON.stringify({ ...EMPTY_SOURCE, ...Object.fromEntries(Object.keys(EMPTY_SOURCE).map(k => [k, source[k]])) }) === JSON.stringify({ ...EMPTY_SOURCE, ...Object.fromEntries(Object.keys(EMPTY_SOURCE).map(k => [k, form[k]])) });
  const proc = processingLabel(source?.processing || 'reference_only');
  return (
    <div style={cardSt}>
      <button className="action-btn" onClick={onBack}>{backLabel}</button>
      <h3>{source ? 'Fuente' : 'Nueva fuente'}</h3>
      {source?.origin && <p style={mutedSt}>Viene de Mi espacio (Biblioteca de vida): se transfirió solo lo que Ale eligió.</p>}
      {source && <p style={mutedSt}><span style={badgeSt(source.processing !== 'reference_only')}>{proc.label}</span> {proc.hint}{source.processedAt ? ` Procesada el ${fmtDate(source.processedAt, true)}.` : ''}</p>}
      {error && <div style={{ ...warnSt, padding: 10 }}>{error}</div>}
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <Field label="Título"><input style={inputSt} value={form.title} maxLength={SOURCE_TEXT_LIMITS.title} onChange={e => set('title', e.target.value)} /></Field>
          <Field label="Autor o creador"><input style={inputSt} value={form.creator} maxLength={SOURCE_TEXT_LIMITS.creator} onChange={e => set('creator', e.target.value)} /></Field>
          <Field label="Tipo"><select style={inputSt} value={form.kind || ''} onChange={e => set('kind', e.target.value)}>{SOURCE_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}</select></Field>
        </div>
        <Field label="Enlace o referencia" hint={SOURCE_FILE_MESSAGES.link}>
          <input style={inputSt} value={form.reference} maxLength={SOURCE_TEXT_LIMITS.reference} onChange={e => set('reference', e.target.value)} />
        </Field>
        <Field label="Temas (separados por coma)"><input style={inputSt} value={(form.topics || []).join(', ')} onChange={e => set('topics', e.target.value.split(',').map(x => x.trim()).filter(Boolean))} /></Field>
        <Field label={<>Mis notas <Counter value={form.notes} max={SOURCE_TEXT_LIMITS.notes} /></>} hint="Lo que aprendiste, en tus palabras.">
          <textarea style={{ ...textareaSt, minHeight: 120 }} value={form.notes} maxLength={SOURCE_TEXT_LIMITS.notes} onChange={e => set('notes', e.target.value)} />
        </Field>
        <div>
          <span style={labelSt}>Fragmentos aportados (con capítulo, página o minuto)</span>
          <div style={mutedSt}>Copia solo lo necesario. Un archivo .txt, .md o .docx se lee en tu navegador y se guarda como fragmento de texto; el archivo no se sube.</div>
          {form.excerpts.map((x, i) => (
            <div key={x.id || i} style={{ display: 'grid', gap: 4, margin: '8px 0', borderLeft: '2px solid var(--globant-border)', paddingLeft: 8 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ ...inputSt, width: 220 }} placeholder="Capítulo, página o minuto" value={x.locator || ''} maxLength={SOURCE_TEXT_LIMITS.locator} onChange={e => set('excerpts', form.excerpts.map((y, j) => (j === i ? { ...y, locator: e.target.value } : y)))} />
                <button className="action-btn" onClick={() => set('excerpts', form.excerpts.filter((_, j) => j !== i))}>✕</button>
              </div>
              <textarea style={textareaSt} value={x.text} maxLength={SOURCE_TEXT_LIMITS.excerpt} onChange={e => set('excerpts', form.excerpts.map((y, j) => (j === i ? { ...y, text: e.target.value } : y)))} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="action-btn" disabled={form.excerpts.length >= SOURCE_EXCERPTS_MAX} onClick={() => set('excerpts', [...form.excerpts, { text: '', locator: '' }])}>+ Fragmento</button>
            <label className="action-btn" style={{ cursor: 'pointer' }}>Leer un archivo (.txt, .md, .docx)<input type="file" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) importFile(e.target.files[0]); e.target.value = ''; }} /></label>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="action-btn btn-primary" disabled={busy || saved} onClick={save}>Guardar fuente</button>
          {source && <button className="action-btn" disabled={busy || !saved || source.processing === 'reference_only'} title={source.processing === 'reference_only' ? 'Agrega notas o fragmentos primero' : ''} onClick={propose}>{busy ? 'Pensando…' : '✨ Proponer herramientas desde este texto'}</button>}
          {source && <button className="action-btn" disabled={busy} onClick={() => call({ action: source.status === 'archived' ? 'unarchiveSource' : 'archiveSource', id: source.id }).then(d => { setSource(d.source); onSaved(d.source); })}>{source.status === 'archived' ? 'Reactivar' : 'Archivar'}</button>}
        </div>
        {source && !saved && <div style={mutedSt}>Guarda los cambios antes de pedir propuestas.</div>}
        {source?.derivedTools?.length > 0 && <div style={mutedSt}>Herramientas derivadas: {source.derivedTools.map(t => `${t.title} (${methodLabel(METHOD_STATUSES, t.status)})`).join(' · ')}</div>}
      </div>
      <SourceProposals proposals={proposals} setProposals={setProposals} sources={sources} onSaved={onToolSaved} />
    </div>
  );
}

function SourcesTab({ sources, reloadSources, onToolSaved }) {
  const [open, setOpen] = useState(undefined); // undefined: list; null: new; id: one
  if (open !== undefined) return <SourceEditor id={open} sources={sources} onBack={() => setOpen(undefined)} onSaved={reloadSources} onToolSaved={onToolSaved} />;
  return (
    <div style={cardSt}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <p style={{ ...mutedSt, margin: 0 }}>Libros, podcasts, videos, cursos, artículos, notas propias y experiencias. Una fuente guardada sin texto es solo una referencia: la IA no la leyó.</p>
        <button className="action-btn btn-primary" onClick={() => setOpen(null)}>+ Fuente</button>
      </div>
      {!sources ? 'Cargando…' : !sources.length ? <div style={mutedSt}>Todavía no hay fuentes.</div> : sources.map(s => (
        <button key={s.id} onClick={() => setOpen(s.id)} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'rgba(30,42,44,0.03)', border: '1px solid var(--globant-border)', borderRadius: 8, padding: 10, color: 'var(--globant-text)', cursor: 'pointer', marginBottom: 8 }}>
          <b style={{ fontSize: 13 }}>{s.title}</b> {s.creator && <span style={mutedSt}>· {s.creator}</span>} <span style={badgeSt(false)}>{methodLabel(SOURCE_KINDS, s.kind)}</span> <span style={badgeSt(s.processing !== 'reference_only')}>{processingLabel(s.processing).label}</span>
          {s.status === 'archived' && <span style={badgeSt(false)}>archivada</span>}
          <div style={{ ...mutedSt, marginTop: 4 }}>{s.topics?.length ? `Temas: ${s.topics.join(', ')} · ` : ''}{s.excerptCount} fragmentos{s.hasNotes ? ' · con notas' : ''} · {s.derivedTools.length} herramientas derivadas</div>
        </button>
      ))}
    </div>
  );
}

export default function MethodLibrary() {
  const [tab, setTab] = useState('list');
  const [entries, setEntries] = useState(null);
  const [toolFields, setToolFields] = useState(true);
  const [sources, setSources] = useState(null);
  const [error, setError] = useState('');
  const [f, setF] = useState({ status: '', applies: '', area: '', modality: '', toolType: '', source: '', topic: '', query: '' });
  // Client criteria of System Design (Fase 7)
  const [industry, setIndustry] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [saleType, setSaleType] = useState('');
  const [country, setCountry] = useState('');
  const [entryType, setEntryType] = useState('');
  const [module, setModule] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = () => call({ action: 'list' }).then(d => { setEntries(d.entries); setToolFields(d.toolFields !== false); }).catch(e => setError(e.message));
  const loadSources = () => call({ action: 'listSources' }).then(d => setSources(d.sources)).catch(() => setSources([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (toolFields) loadSources(); else setSources([]); }, [toolFields]);

  const topics = useMemo(() => [...new Set((entries || []).flatMap(e => e.topics || []))].sort(), [entries]);
  const shown = useMemo(() => {
    const q = f.query.trim().toLowerCase();
    // A criterion filter shows the entries that would fit that value, plus the general ones; "general" shows only those
    const fits = (list, v) => !v || (v === '_general' ? !list.length : (!list.length || list.includes(v)));
    return (entries || []).filter(e => (!f.status || e.status === f.status) && (!f.applies || e.appliesTo.includes(f.applies))
      && (!f.area || (e.areas || []).includes(f.area)) && (!f.toolType || e.toolType === f.toolType)
      && (!f.modality || e.modality === f.modality || (e.modality === 'both' && f.modality !== 'both'))
      && (!f.source || (e.sourceRefs || []).some(r => r.sourceId === f.source)) && (!f.topic || (e.topics || []).includes(f.topic))
      && (!entryType || (e.entryType || 'technique') === entryType)
      && (industry === '_general' ? isGeneral(e) : fits(e.industries || [], industry))
      && fits(e.teamSizes || [], teamSize) && fits(e.saleTypes || [], saleType)
      && (!country || country === '_general' || !(e.countries || []).length || e.countries.includes(country) || (e.countries || []).some(c => METHOD_REGIONS[c]?.includes(country)))
      && (!module || !(e.sdModules || []).length || e.sdModules.includes(module))
      && (!q || [e.title, e.technique, e.purpose, e.example, e.whenToUse, e.whenNotToUse, e.source, ...(e.topics || [])].some(x => (x || '').toLowerCase().includes(q))));
  }, [entries, f, industry, teamSize, saleType, country, entryType, module]);
  const sel = (k, list, all) => (
    <select style={{ ...inputSt, width: 'auto' }} value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })}>
      <option value="">{all}</option>
      {list.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
    </select>
  );

  const upsert = (entry) => { setEntries(list => [entry, ...(list || []).filter(x => x.id !== entry.id)]); if (toolFields) loadSources(); };

  return (
    <div className="page-content" style={{ padding: 20, maxWidth: 1100 }}>
      <PageHeader icon="📚" area="Acompañamiento" title="Biblioteca del método" />
      <p style={mutedSt}>
        Técnicas y herramientas que la IA consulta cuando redacta o recomienda: fichas e informe del Diagnostic, mensajes, propuestas y preparación
        de sesiones de acompañamiento. Solo entran las entradas activas. La rúbrica no está acá: cambia solo por código y con número de versión.
      </p>
      {!toolFields && <div style={{ ...warnSt, padding: 10, fontSize: 13 }}>Los campos de herramienta y las fuentes todavía no existen en Airtable. Todo lo actual sigue funcionando; para usarlos, aplica la migración (docs/coaching/configuracion.md).</div>}
      <SubTabs tabs={[['list', 'Entradas'], ['new', '+ Nueva'], ...(toolFields ? [['sources', 'Fuentes']] : []), ['notes', '✨ Crear desde mis notas']]} value={tab} onChange={t => { setTab(t); setOpenId(null); }} />
      {error && <div style={{ ...warnSt, padding: 10 }}>{error}</div>}

      {tab === 'new' && <NewEntry toolFields={toolFields} sources={sources || []} onCancel={() => setTab('list')} onSaved={e => { upsert(e); setTab('list'); setOpenId(e.id); }} />}
      {tab === 'notes' && <FromNotes onSaved={upsert} toolFields={toolFields} />}
      {tab === 'sources' && <SourcesTab sources={sources} reloadSources={loadSources} onToolSaved={upsert} />}

      {tab === 'list' && openId && <EntryDetail id={openId} toolFields={toolFields} sources={sources || []} onBack={() => setOpenId(null)} onChanged={upsert} />}
      {tab === 'list' && !openId && (
        <div style={cardSt}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <input style={{ ...inputSt, flex: '1 1 220px', width: 'auto' }} placeholder="Buscar en título, propósito, técnica, temas…" value={f.query} onChange={e => setF({ ...f, query: e.target.value })} />
            {sel('status', METHOD_STATUSES, 'Todos los estados')}
            {sel('applies', METHOD_APPLIES_TO, 'Se usa en todo')}
            {toolFields && <>
              {sel('toolType', METHOD_TOOL_TYPES, 'Todos los tipos')}
              {sel('area', METHOD_AREAS, 'Todas las áreas')}
              {sel('modality', METHOD_MODALITIES, 'Toda modalidad')}
              {topics.length > 0 && sel('topic', topics.map(t => ({ key: t, label: t })), 'Todos los temas')}
              {sources?.length > 0 && sel('source', sources.map(s => ({ key: s.id, label: s.title })), 'Todas las fuentes')}
            </>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <select style={{ ...inputSt, width: 'auto' }} value={industry} onChange={e => setIndustry(e.target.value)}>
              <option value="">Toda industria</option><option value="_general">Solo generales</option>
              {METHOD_INDUSTRIES.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>
            <select style={{ ...inputSt, width: 'auto' }} value={teamSize} onChange={e => setTeamSize(e.target.value)}>
              <option value="">Todo tamaño</option>
              {METHOD_TEAM_SIZES.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>
            <select style={{ ...inputSt, width: 'auto' }} value={saleType} onChange={e => setSaleType(e.target.value)}>
              <option value="">Todo tipo de venta</option>
              {METHOD_SALE_TYPES.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>
            <select style={{ ...inputSt, width: 'auto' }} value={country} onChange={e => setCountry(e.target.value)}>
              <option value="">Todo país</option>
              {METHOD_COUNTRIES.filter(c => !METHOD_REGIONS[c.key]).map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>
            <select style={{ ...inputSt, width: 'auto' }} value={entryType} onChange={e => setEntryType(e.target.value)}>
              <option value="">Técnicas y frameworks</option>
              {METHOD_ENTRY_TYPES.map(a => <option key={a.key} value={a.key}>{a.key === 'framework' ? 'Solo frameworks' : 'Solo técnicas'}</option>)}
            </select>
            <select style={{ ...inputSt, width: 'auto' }} value={module} onChange={e => setModule(e.target.value)}>
              <option value="">Todo módulo de System Design</option>
              {METHOD_SD_MODULES.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>
          </div>
          {!entries ? <div style={mutedSt}>Cargando…</div> : !shown.length ? <div style={mutedSt}>No hay entradas con esos filtros.</div> : (
            <div style={{ display: 'grid', gap: 8 }}>
              {shown.map(e => (
                <button key={e.id} onClick={() => setOpenId(e.id)}
                  style={{ textAlign: 'left', background: 'rgba(30,42,44,0.03)', border: '1px solid var(--globant-border)', borderRadius: 8, padding: 10, color: 'var(--globant-text)', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <b style={{ fontSize: 13 }}>{e.title || 'Sin título'}</b>
                    <StatusBadge status={e.status} /><span style={mutedSt}>v{e.version}</span>
                    {e.toolType && <span style={badgeSt(false)}>{methodLabel(METHOD_TOOL_TYPES, e.toolType)}</span>}
                    {e.requiresTraining && <span style={badgeSt(false)}>requiere formación</span>}
                    {e.clientVisible && <span style={badgeSt(false)}>visible para clientes</span>}
                    {e.entryType === 'framework' && <span style={badgeSt(true)}>framework</span>}
                    <span style={badgeSt(!isGeneral(e))}>{isGeneral(e) ? 'general' : 'específica'}</span>
                  </div>
                  {!isGeneral(e) && <div style={{ ...mutedSt, marginTop: 4 }}>{criteriaOf(e).join(' · ')}</div>}
                  <div style={{ ...mutedSt, marginTop: 4 }}>
                    {e.appliesTo.map(k => methodLabel(METHOD_APPLIES_TO, k)).join(' · ') || 'Sin lugar asignado'}
                    {e.areas?.length ? ` · ${e.areas.map(k => methodLabel(METHOD_AREAS, k)).join(', ')}` : ''}
                    {e.modality ? ` · ${methodLabel(METHOD_MODALITIES, e.modality)}` : ''} · actualizada {fmtDate(e.updatedAt || e.created)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
